"use client";

import { useReportWebVitals } from "next/web-vitals";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import moment from "moment";
import { getBasePath } from "@/config";

export function formatWebVital(metric) {
  const map = {
    FCP: {
      fullName: "First Contentful Paint",
      unit: "ms",
      thresholds: { good: 1800, needsImprovement: 3000 },
    },
    LCP: {
      fullName: "Largest Contentful Paint",
      unit: "ms",
      thresholds: { good: 2500, needsImprovement: 4000 },
    },
    CLS: {
      fullName: "Cumulative Layout Shift",
      unit: "",
      thresholds: { good: 0.1, needsImprovement: 0.25 },
    },
    INP: {
      fullName: "Interaction to Next Paint",
      unit: "ms",
      thresholds: { good: 200, needsImprovement: 500 },
    },
    TTFB: {
      fullName: "Time to First Byte",
      unit: "ms",
      thresholds: { good: 800, needsImprovement: 1800 },
    },
  };

  const m = map[metric.name] || {
    fullName: metric.name,
    unit: "",
    thresholds: {},
  };

  // Format numeric value
  const formattedValue =
    m.unit === "ms" ? metric.value.toFixed(0) + " ms" : metric.value.toFixed(3);

  // Interpret result
  let rating = "good";
  if (m.thresholds.good && metric.value > m.thresholds.needsImprovement) {
    rating = "poor";
  } else if (m.thresholds.good && metric.value > m.thresholds.good) {
    rating = "needs improvement";
  }

  return {
    name: metric.name,
    fullName: m.fullName,
    value: metric.value,
    formattedValue,
    rating,
    page: window.location.pathname,
    timestamp: moment().toISOString(),
  };
}

async function sendToAnalytics(metric) {
  const formattedMetric = formatWebVital(metric);
  const body = JSON.stringify(formattedMetric);

  console.log("Web Vital Metric:", formattedMetric);
  const basePath = getBasePath();

  // Send using sendBeacon (preferred)
  const URL = basePath
    ? `${basePath}/api/analytics/web-vitals`
    : "/api/analytics/web-vitals";
  if (navigator.sendBeacon) {
    navigator.sendBeacon(URL, body);
  } else {
    // Fallback to fetch if sendBeacon is not supported
    fetch(URL, {
      method: "POST",
      body,
      keepalive: true, // keepalive lets it continue even after page unload
      headers: { "Content-Type": "application/json" },
    });
  }
}

export function WebVitals() {
  const pathname = usePathname();
  useReportWebVitals(sendToAnalytics);

  useEffect(() => {
    // Send a lightweight page-view event
    const URL = getBasePath()
      ? `${getBasePath()}/api/analytics/page-view`
      : "/api/analytics/page-view";
    if (pathname) {
      navigator.sendBeacon(
        URL,
        JSON.stringify({
          path: pathname,
          timestamp: Date.now(),
        }),
      );
    }
  }, [pathname]);

  return null;
}
